import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ShoppingCart, Plus, Minus, Search, Package2, X, Check, Mail, AlertCircle, Trash2, CreditCard, Send, MessageSquare, RefreshCw, Tag } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { getStockItems, StockItem } from '../lib/stockItems';
import { fetchCustomerPriceMap } from '../lib/customerPricing';
import { ProductPhotoService } from '../services/productPhotoService';
import { DiscountCodeService } from '../services/discountCodeService';
import { toast } from '../components/toasts/SimpleToast';
import SelectCustomerModal from '../components/manage/SelectCustomerModal';
import RecordProductSalePaymentModal from '../components/RecordProductSalePaymentModal';
import InvoiceEmailModal from '../components/InvoiceEmailModal';
import InvoiceSMSModal from '../components/InvoiceSMSModal';
import {
  checkoutCounterSale,
  quoteCounterCheckout,
  toCounterSaleDiscount,
  CounterCheckoutQuote,
  CounterCheckoutLine,
  CounterCheckoutPayment,
  CounterCheckoutResult,
  CounterRefundMode,
  CounterReturnInput,
} from '../lib/productSales';
import RefundDestinationSelect from '../components/refunds/RefundDestinationSelect';
import { sendEmailWithFallback } from '../lib/email';
import { sendSMS } from '../lib/messaging';
import { supabase } from '../lib/supabase';

// Custom portal-based toast component for modal contexts
const PortalToast = ({ 
  children, 
  type, 
  onClose 
}: { 
  children: React.ReactNode; 
  type: 'loading' | 'success' | 'error';
  onClose: () => void;
}) => {
  const getIcon = () => {
    switch (type) {
      case 'loading':
        return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>;
      case 'success':
        return <div className="rounded-full h-4 w-4 bg-green-500 flex items-center justify-center text-white text-xs">✓</div>;
      case 'error':
        return <div className="rounded-full h-4 w-4 bg-red-500 flex items-center justify-center text-white text-xs">✕</div>;
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'loading':
        return 'border-blue-200';
      case 'success':
        return 'border-green-200';
      case 'error':
        return 'border-red-200';
    }
  };

  return createPortal(
    <div 
      className={`fixed left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg border ${getBorderColor()} p-4 flex items-center space-x-3 min-w-[300px] max-w-md`}
      style={{ zIndex: 999999, top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
    >
      {getIcon()}
      <div className="flex-1">
        {children}
      </div>
      {type !== 'loading' && (
        <button 
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 ml-2"
        >
          ×
        </button>
      )}
    </div>,
    document.body
  );
};

// Custom toast function with portal rendering for modal contexts
const createPortalToast = (
  setShowPortalToast: (show: boolean) => void,
  setPortalToastMessage: (message: React.ReactNode) => void,
  setPortalToastType: (type: 'loading' | 'success' | 'error') => void
) => ({
  loading: (message: React.ReactNode, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('loading');
    setShowPortalToast(true);
    return 'portal-toast-loading';
  },
  success: (message: string, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('success');
    setShowPortalToast(true);
    // Auto-hide success toast after 4 seconds
    setTimeout(() => setShowPortalToast(false), 4000);
    return 'portal-toast-success';
  },
  error: (message: string, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('error');
    setShowPortalToast(true);
    // Auto-hide error toast after 4 seconds
    setTimeout(() => setShowPortalToast(false), 4000);
    return 'portal-toast-error';
  },
  dismiss: (toastId?: string) => {
    setShowPortalToast(false);
  }
});

interface CartItem {
  product: StockItem;
  quantity: number;
  /** Hand the item over on a recurring rental agreement instead of selling it. */
  isRental: boolean;
}

/** Rentable: the item is set up to rent and has a rate. */
const isRentable = (i: Pick<StockItem, 'rental_enabled' | 'rental_price_cents'>) =>
  i.rental_enabled === true && Number(i.rental_price_cents) > 0;
/** Rental-only: rentable with no sale price — it can only go out as a rental. */
const isRentalOnly = (i: Pick<StockItem, 'rental_enabled' | 'rental_price_cents' | 'price'>) =>
  isRentable(i) && !(Number(i.price) > 0);
const rentalRate = (i: Pick<StockItem, 'rental_price_cents'>) => Number(i.rental_price_cents ?? 0) / 100;
const rentalPeriod = (i: Pick<StockItem, 'rental_interval'>) => (i.rental_interval === 'week' ? 'week' : 'month');
const rentalRateLabel = (i: Pick<StockItem, 'rental_price_cents' | 'rental_interval'>) =>
  `$${rentalRate(i).toFixed(2)}/${i.rental_interval === 'week' ? 'wk' : 'mo'}`;
const fmtCents = (cents: number) => `$${(cents / 100).toFixed(2)}`;

/** Per deposit item: what staff entered. `touched` = typed/clicked by staff,
 *  so it is never overwritten by a recomputed default. */
type ReturnsState = Record<string, { returned: string; lost: string; touched?: boolean }>;

function SellProducts() {
  const { selectedBusiness } = useBusiness();
  const [items, setItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  
  // Customer and payment states
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  // Customer pricing: stock_item_id → the selected customer's custom/tier price.
  // Cart lines reprice when the customer changes; catalog restores on clear.
  const [customerPriceMap, setCustomerPriceMap] = useState<Record<string, number>>({});
  // Wallet credit on file — applied first to the sale invoice (wallet-first),
  // so the amount to collect at the counter is total minus this.
  const [customerWallet, setCustomerWallet] = useState(0);
  // Container deposits, adjustable on screen exactly like the order Deliver
  // step: empties the customer hands back (and any lost) per deposit item.
  // Defaults to a full swap against what they hold on record; the server
  // rebuilds the deposit lines from these numbers.
  const [returns, setReturns] = useState<ReturnsState>({});
  // Where an excess-return deposit refund goes. Auto-picked: wallet credit
  // for customers, cash for walk-ins; staff can change it from the footer.
  const [refundMode, setRefundMode] = useState<CounterRefundMode>('cash');
  const [refundReference, setRefundReference] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let map: Record<string, number> = {};
      let wallet = 0;
      if (selectedCustomer?.id && selectedBusiness?.id) {
        try {
          map = await fetchCustomerPriceMap(selectedBusiness.id, selectedCustomer.id);
        } catch { /* fall back to catalog */ }
        try {
          const { data } = await supabase.from('customers').select('wallet').eq('id', selectedCustomer.id).maybeSingle();
          wallet = Number(data?.wallet || 0);
        } catch { /* no wallet preview */ }
      }
      if (cancelled) return;
      setCustomerPriceMap(map);
      setCustomerWallet(wallet);
      // Empties defaults depend on the customer's container record: drop only
      // the untouched defaults so they recompute; anything staff typed stays.
      setReturns(prev => Object.fromEntries(Object.entries(prev).filter(([, v]) => v.touched)));
      // Refund destination follows the customer: store credit when there is an
      // account, cash for a walk-in.
      setRefundMode(selectedCustomer?.id ? 'wallet' : 'cash');
      setCart(prev =>
        prev.map(ci => {
          const catalog = items.find(p => p.id === ci.product.id)?.price ?? ci.product.price;
          const price = map[ci.product.id] ?? catalog;
          return price === ci.product.price ? ci : { ...ci, product: { ...ci.product, price } };
        })
      );
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer?.id, selectedBusiness?.id]);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);
  const [loadingToastId, setLoadingToastId] = useState<string | null>(null);
  const [showSendDropdown, setShowSendDropdown] = useState(false);
  const [isInvoiceEmailModalOpen, setIsInvoiceEmailModalOpen] = useState(false);
  const [isInvoiceSMSModalOpen, setIsInvoiceSMSModalOpen] = useState(false);
  const [shouldOpenSendInvoiceAfterCustomer, setShouldOpenSendInvoiceAfterCustomer] = useState<'email' | 'sms' | null>(null);
  const [showPortalToast, setShowPortalToast] = useState(false);
  const [portalToastMessage, setPortalToastMessage] = useState<React.ReactNode>(null);
  const [portalToastType, setPortalToastType] = useState<'loading' | 'success' | 'error'>('loading');

  // Create portal toast instance
  const portalToast = createPortalToast(setShowPortalToast, setPortalToastMessage, setPortalToastType);

  // Close send dropdown on outside click
  useEffect(() => {
    if (!showSendDropdown) return;
    let mounted = true;
    const close = () => { if (mounted) setShowSendDropdown(false); };
    const raf = requestAnimationFrame(() => {
      document.addEventListener('mousedown', close);
    });
    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', close);
    };
  }, [showSendDropdown]);

  // Discount-related state
  const [showDiscountEditor, setShowDiscountEditor] = useState(false);
  const [discountMode, setDiscountMode] = useState<'code' | 'custom'>('code');
  const [discountCode, setDiscountCode] = useState('');
  const [customDiscountAmount, setCustomDiscountAmount] = useState('');
  const [customDiscountType, setCustomDiscountType] = useState<'%' | '$'>('%');
  const [appliedDiscount, setAppliedDiscount] = useState<{
    id?: string;
    code?: string;
    type: 'percentage' | 'fixed';
    value: number;
    description?: string;
    discountAmount: number;
    source: 'code' | 'custom';
  } | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [discountValidating, setDiscountValidating] = useState(false);

  // Server-computed totals: business tax rates, per-product exemption, deposit
  // lines rebuilt from the empties entered, excess refund routing.
  const [cartQuote, setCartQuote] = useState<CounterCheckoutQuote | null>(null);

  // Load stock items
  useEffect(() => {
    if (selectedBusiness?.id) {
      loadItems();
    }
  }, [selectedBusiness?.id]);

  /** Cart → engine lines. Rental lines carry the per-period rate. */
  const buildCheckoutItems = (): CounterCheckoutLine[] =>
    cart.map(ci =>
      ci.isRental
        ? {
            stock_item_id: ci.product.id,
            product_name: `Rental — ${ci.product.name} (first ${rentalPeriod(ci.product)})`,
            quantity: ci.quantity,
            unit_price: rentalRate(ci.product),
            line_type: 'rental' as const,
          }
        : {
            stock_item_id: ci.product.id,
            product_name: ci.product.name,
            quantity: ci.quantity,
            unit_price: ci.product.price,
          }
    );

  const returnsPayload = (r: ReturnsState): CounterReturnInput[] | null => {
    const entries = Object.entries(r);
    if (entries.length === 0) return null;
    return entries.map(([stock_item_id, v]) => ({
      stock_item_id,
      returned: Math.max(0, parseInt(v.returned) || 0),
      lost: Math.max(0, parseInt(v.lost) || 0),
    }));
  };

  // Quote the cart against the fulfillment engine whenever it changes
  useEffect(() => {
    if (!selectedBusiness?.id || cart.length === 0) {
      setCartQuote(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const quote = await quoteCounterCheckout({
          businessId: selectedBusiness.id,
          customerId: selectedCustomer?.id ?? null,
          items: buildCheckoutItems(),
          discount: toCounterSaleDiscount(appliedDiscount),
          returns: returnsPayload(returns),
          refundMode,
        });
        if (cancelled) return;
        setCartQuote(quote);
        // First sight of a deposit item: default to a full swap against the
        // containers on record (the online-store assumption); staff adjust.
        setReturns(prev => {
          let next = prev;
          for (const d of quote.deposits) {
            if (next[d.stock_item_id]) continue;
            const swap = Math.min(d.prior_balance ?? 0, d.delivered);
            next = { ...next, [d.stock_item_id]: { returned: String(swap), lost: '0' } };
          }
          return next;
        });
      } catch (error) {
        console.error('Failed to quote cart totals:', error);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, appliedDiscount, returns, refundMode, selectedCustomer?.id, selectedBusiness?.id]);

  const loadItems = async () => {
    if (!selectedBusiness?.id) return;
    
    setLoading(true);
    try {
      const stockItems = await getStockItems(selectedBusiness.id);
      
      // Show in-stock items plus untracked (made-to-order) and oversellable
      // items, which are always sellable regardless of quantity
      const sellableItems = stockItems.filter(item =>
        (item.quantity > 0 || item.track_inventory === false || item.allow_oversell === true) &&
        !item.is_deleted
      );
      setItems(sellableItems);
      
      // Load product photos
      const stockItemIds = sellableItems.map(item => item.id);
      if (stockItemIds.length > 0) {
        try {
          const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
          
          // Convert to the format expected by the component
          const photoUrls: Record<string, string> = {};
          Object.values(photoMap).forEach(photo => {
            photoUrls[photo.stock_item_id] = photo.photo_url;
          });
          
          setProductPhotos(photoUrls);
        } catch (error) {
          console.warn('Failed to load product photos:', error);
        }
      }
    } catch (error) {
      console.error('Failed to load stock items:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter items based on search
  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.sku?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Cart functions
  const addToCart = (product: StockItem) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product.id === product.id);
      if (existingItem) {
        // Check if we can add more (untracked/oversellable items have no cap)
        if (product.track_inventory !== false && product.allow_oversell !== true && existingItem.quantity >= product.quantity) {
          return prevCart;
        }
        return prevCart.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        // Selected customer's custom/tier price wins over catalog.
        const price = customerPriceMap[product.id] ?? product.price;
        // Rental-only gear goes out as a rental; dual-purpose items start as
        // a sale with a Rent toggle on the cart line.
        return [...prevCart, {
          product: price === product.price ? product : { ...product, price },
          quantity: 1,
          isRental: isRentalOnly(product),
        }];
      }
    });
  };

  const removeFromCart = (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.product.id !== productId));
  };

  /** Flip a rentable line between one-time sale and recurring rental. */
  const setLineRental = (productId: string, rent: boolean) => {
    setCart(prev => prev.map(ci => (ci.product.id === productId ? { ...ci, isRental: rent } : ci)));
  };

  const updateCartQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    const product = items.find(item => item.id === productId);
    if (product && product.track_inventory !== false && product.allow_oversell !== true && quantity > product.quantity) {
      return;
    }

    setCart(prevCart =>
      prevCart.map(item =>
        item.product.id === productId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    setAppliedDiscount(null);
    setDiscountCode('');
    setCustomDiscountAmount('');
    setDiscountError(null);
    setShowDiscountEditor(false);
    setReturns({});
    setRefundMode(selectedCustomer?.id ? 'wallet' : 'cash');
    setRefundReference('');
  };

  // Discount functions
  const applyDiscountCode = async () => {
    if (!discountCode.trim()) return;
    
    setDiscountValidating(true);
    setDiscountError(null);
    
    try {
      console.log('🔍 Validating discount code:', discountCode, 'for business:', selectedBusiness?.id, 'subtotal:', cartSubtotal);
      const validation = await DiscountCodeService.validateDiscountCode(selectedBusiness?.id || '', discountCode.toUpperCase(), cartSubtotal);
      console.log('✅ Discount validation result:', validation);
      
      if (validation.isValid && validation.code) {
        const discount = validation.code;
        const discountAmount = validation.discountAmount || 0;
          
        setAppliedDiscount({
          id: discount.id,
          code: discount.code,
          type: discount.type,
          value: discount.value,
          description: discount.description,
          discountAmount: discountAmount,
          source: 'code'
        });
        setDiscountCode('');
        setShowDiscountEditor(false);
      } else {
        console.log('❌ Discount validation failed:', validation.error);
        setDiscountError(validation.error || 'Invalid discount code');
      }
    } catch (error: any) {
      console.error('❌ Discount code validation error:', error);
      setDiscountError(error.message || 'Invalid discount code');
    } finally {
      setDiscountValidating(false);
    }
  };

  const applyCustomDiscount = () => {
    const amount = parseFloat(customDiscountAmount);
    if (isNaN(amount) || amount <= 0) {
      setDiscountError('Please enter a valid discount amount');
      return;
    }

    const discountAmount = customDiscountType === '%' 
      ? (cartSubtotal * amount) / 100
      : amount;

    if (discountAmount > cartSubtotal) {
      setDiscountError('Discount cannot exceed subtotal');
      return;
    }

    setAppliedDiscount({
      type: customDiscountType === '%' ? 'percentage' : 'fixed',
      value: amount,
      description: `Custom ${customDiscountType === '%' ? amount + '%' : '$' + amount} discount`,
      discountAmount,
      source: 'custom'
    });
    
    setCustomDiscountAmount('');
    setDiscountError(null);
    setShowDiscountEditor(false);
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountError(null);
  };

  // Calculate totals. Tax and deposits come from the server quote (business
  // tax rates, per-product exemption, deposit lines rebuilt from the empties
  // entered) so the displayed total matches the invoice exactly.
  const cartSubtotal = cart.reduce(
    (total, item) => total + ((item.isRental ? rentalRate(item.product) : item.product.price) * item.quantity),
    0
  );
  const discountAmount = appliedDiscount ? appliedDiscount.discountAmount : 0;
  const discountedSubtotal = cartSubtotal - discountAmount;
  const cartTaxAmount = cartQuote?.tax ?? 0;
  const cartTotal = cartQuote ? cartQuote.total : discountedSubtotal;
  const depositChargedCents = cartQuote ? cartQuote.deposits.reduce((n, d) => n + d.net_new * d.deposit_cents, 0) : 0;
  const excessRefundCents = cartQuote?.excessRefundCents ?? 0;
  const invoiceCreditCents = cartQuote?.invoiceCreditCents ?? 0;
  const hasRental = cart.some(ci => ci.isRental);
  // Wallet-first preview: credit on file plus any refund credited this visit.
  const totalCents = Math.round(cartTotal * 100);
  const walletAvailableCents = Math.round(customerWallet * 100) + (refundMode === 'wallet' ? excessRefundCents : 0);
  const walletWillApplyCents = selectedCustomer ? Math.min(walletAvailableCents, totalCents) : 0;
  const amountToCollect = Math.max(totalCents - walletWillApplyCents, 0) / 100;
  const taxLabel = cartQuote && cartQuote.combinedRatePercent > 0 ? `Tax (${cartQuote.combinedRatePercent}%)` : 'Tax';

  /**
   * The one checkout call every path uses (Take Payment, email, SMS). Same
   * engine as an order delivery: deposits from the empties entered, container
   * ledger, wallet-first, rentals activated — no order row.
   */
  const runCheckout = async (payment: CounterCheckoutPayment | null): Promise<CounterCheckoutResult> => {
    if (!selectedBusiness?.id) throw new Error('No business selected');
    return checkoutCounterSale({
      businessId: selectedBusiness.id,
      customerId: selectedCustomer?.id ?? null,
      items: buildCheckoutItems(),
      discount: toCounterSaleDiscount(appliedDiscount),
      returns: returnsPayload(returns),
      payment,
      expectedTotalCents: cartQuote ? Math.round(cartQuote.total * 100) : null,
      refundMode,
      notes: refundReference.trim() ? `Counter sale — refund ref: ${refundReference.trim()}` : 'Counter sale',
    });
  };

  const [isCompletingWithWallet, setIsCompletingWithWallet] = useState(false);
  /** Wallet credit covers the whole sale — nothing to collect, just settle. */
  const completeWithWallet = async () => {
    if (isCompletingWithWallet) return;
    setIsCompletingWithWallet(true);
    try {
      const res = await runCheckout(null);
      portalToast.success(
        res.wallet_applied_cents > 0
          ? `Sale ${res.sale_number} complete — paid from wallet credit`
          : `Sale ${res.sale_number} complete — nothing to collect`
      );
      clearCart();
      setSelectedCustomer(null);
      loadItems();
    } catch (error: any) {
      portalToast.error(error?.message || 'Failed to complete sale');
    } finally {
      setIsCompletingWithWallet(false);
    }
  };

  const handleSendInvoiceVia = (method: 'email' | 'sms') => {
    if (!selectedCustomer) {
      setShouldOpenSendInvoiceAfterCustomer(method);
      setIsCustomerModalOpen(true);
      return;
    }
    if (method === 'email') {
      setIsInvoiceEmailModalOpen(true);
    } else {
      setIsInvoiceSMSModalOpen(true);
    }
  };

  // The real invoice (deposit / rental / credit lines, wallet payments, the
  // invoice number) — same generator and mode the order Deliver flow uses.
  const generateProductInvoicePDF = async (sale: { id: string; sale_number: string; invoice_id: string | null; invoice_number?: string | null }): Promise<{ blob: Blob; filename: string } | null> => {
    try {
      const { data, error } = await supabase.functions.invoke('generate-invoice-pdf', {
        body: sale.invoice_id ? { invoiceId: sale.invoice_id } : { productSaleId: sale.id }
      });
      if (error || !data?.base64) {
        console.error('Edge function PDF error:', error);
        return null;
      }
      const bin = atob(data.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      return { blob, filename: data.filename || `invoice-${sale.invoice_number || sale.sale_number}.pdf` };
    } catch (error) {
      console.error('Error generating product invoice PDF:', error);
      return null;
    }
  };

  // Send history — shows up in the invoice viewer like booking / order sends.
  const recordInvoiceSend = async (args: { invoiceId: string; via: 'email' | 'sms'; to: string; paymentUrl: string | null }) => {
    if (!selectedBusiness?.id) return;
    try {
      await supabase.from('invoice_history').insert({
        business_id: selectedBusiness.id,
        invoice_id: args.invoiceId,
        sent_via: args.via,
        has_payment_link: Boolean(args.paymentUrl),
        payment_link_url: args.paymentUrl,
        customer_email: args.via === 'email' ? args.to : null,
        customer_name: selectedCustomer
          ? `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim() || null
          : null,
      });
    } catch (e) {
      console.error('Failed to record invoice send history:', e);
    }
  };

  const handleSendInvoiceEmail = async (
    emailSubject: string,
    emailMessage: string,
    allowStripePay: boolean,
    depositAmount?: number,
    saveCardOnFile?: boolean,
  ) => {
    if (!selectedCustomer?.email || !selectedBusiness) {
      portalToast.error('Missing customer email or business info');
      return;
    }

    if (cart.length === 0) {
      portalToast.error('Cart is empty');
      return;
    }

    setIsInvoiceEmailModalOpen(false);

    try {
      setIsSendingInvoice(true);
      
      const loadingToast = portalToast.loading(
        <div>
          <div>Sending invoice...</div>
          <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>don't close store</div>
        </div>
      );

      // Settle the sale with the invoice left open — the email carries the
      // PDF and (optionally) a pay link for the balance after wallet credit.
      const checkout = await runCheckout(null);
      const balanceDue = Math.max(checkout.total_cents - checkout.wallet_applied_cents, 0) / 100;
      const productSaleWithItems = {
        id: checkout.sale_id,
        sale_number: checkout.sale_number,
        invoice_id: checkout.invoice_id,
        invoice_number: checkout.invoice_number,
        total_amount: checkout.total_cents / 100,
      };

      // The real invoice number in the subject (the modal's default subject is
      // a placeholder written before the invoice exists).
      const finalEmailSubject = emailSubject.includes('Product Invoice')
        ? `Invoice #${checkout.invoice_number} from ${selectedBusiness.name}${balanceDue <= 0 ? ' - Paid' : ''}`
        : emailSubject;
      let paymentUrl: string | null = null;

      // Now generate PDF with the actual sale data
      console.log('📄 Starting PDF generation...');
      const pdfData = await generateProductInvoicePDF(productSaleWithItems);
      if (!pdfData) {
        console.error('❌ Failed to generate PDF');
        throw new Error('PDF generation failed');
      }
      console.log('✅ PDF generated successfully:', pdfData.filename);

      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result as string;
          // Remove data:application/pdf;base64, prefix
          const base64Content = base64String.split(',')[1];
          resolve(base64Content);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(pdfData.blob);
      
      const base64Content = await base64Promise;

      console.log('PDF attachment prepared:', {
        filename: pdfData.filename,
        contentLength: base64Content.length,
        contentPreview: base64Content.substring(0, 50) + '...'
      });

      // Generate payment link if Stripe Connect is enabled and allowStripePay is true
      let paymentLinkHtml = '';

      if (balanceDue > 0 && allowStripePay && (selectedBusiness as any).stripe_connect_account_id && (selectedBusiness as any).online_payments_enabled) {
        console.log('🔍 Creating Stripe payment link for product sale...');
        
        try {
          // Create payment record linked to the product sale
          const { data: pendingPayment, error: paymentError } = await supabase
            .from('payments')
            .insert({
              business_id: selectedBusiness.id,
              booking_id: null, // NULL for product sales
              product_sale_id: productSaleWithItems.id, // Direct foreign key link
              invoice_id: productSaleWithItems.invoice_id ?? null, // settle the counter-sale invoice on completion
              amount: balanceDue,
              method: 'credit_card',
              status: 'pending',
              reference: `Product Sale ${productSaleWithItems.sale_number}`,
              notes: `Payment for product sale ${productSaleWithItems.sale_number} - Product invoice`,
              payment_token: crypto.randomUUID(),
              save_card_on_file: !!(saveCardOnFile && selectedCustomer?.id && allowStripePay),
              save_card_customer_id:
                saveCardOnFile && selectedCustomer?.id && allowStripePay ? selectedCustomer.id : null,
            })
            .select()
            .single();

          if (paymentError) {
            console.error('Failed to create payment record:', paymentError);
            console.log('⚠️ Continuing without payment link due to database constraint');
          } else {
            console.log('✅ Pending payment record created:', pendingPayment.id);

            // Generate payment link
            const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
              body: {
                paymentId: pendingPayment.id
              }
            });

            if (!linkError && linkData?.paymentUrl) {
              paymentUrl = linkData.paymentUrl as string;
              paymentLinkHtml = `
                <div style="margin: 30px 0; padding: 25px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px; text-align: center;">
                  <h3 style="color: white; margin: 0 0 15px 0; font-size: 18px;">Pay Your Invoice Online</h3>
                  <p style="color: #e2e8f0; margin: 0 0 20px 0; font-size: 14px;">Secure payment with credit or debit card</p>
                  <a href="${linkData.paymentUrl}" 
                     style="display: inline-block; background: #ffffff; color: #667eea; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                    Pay $${balanceDue.toFixed(2)} Now
                  </a>
                  <p style="color: #cbd5e0; margin: 15px 0 0 0; font-size: 12px;">Powered by Stripe - Secure &amp; Encrypted</p>
                </div>
              `;
              console.log('✅ Payment link generated:', linkData.paymentUrl);
            } else {
              console.error('Failed to generate payment link:', linkError);
            }
          }
        } catch (paymentLinkError) {
          console.error('Error in payment link generation:', paymentLinkError);
          console.log('⚠️ Continuing without payment link');
        }
      }

      // Build HTML email body from user-edited message
      const messageHtml = emailMessage
        .split('\n')
        .map(line => `<p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 8px 0;">${line || '&nbsp;'}</p>`)
        .join('\n');

      const emailBody = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
          <div style="padding: 40px 32px 32px 32px;">
            ${messageHtml}
            ${paymentLinkHtml}
          </div>
          <div style="padding: 24px 32px; background: #f9fafb; border-top: 1px solid #e5e7eb;">
            <p style="color: #9ca3af; font-size: 13px; margin: 0; text-align: center; line-height: 1.5;">This is an automated invoice from ${selectedBusiness?.name || 'Business'}</p>
          </div>
        </div>
      `;

      // Gmail first, SendGrid fallback — same sender path orders and bookings use.
      console.log('📧 Sending email with attachment...');
      const emailResult = await sendEmailWithFallback({
        businessId: selectedBusiness.id,
        to: selectedCustomer.email,
        subject: finalEmailSubject,
        body: emailBody,
        attachments: [{
          filename: pdfData.filename,
          content: base64Content,
          type: 'application/pdf'
        }],
        customerId: selectedCustomer.id || undefined,
      });
      console.log('📧 Email result:', emailResult);

      if (emailResult.success) {
        if (checkout.invoice_id) {
          await recordInvoiceSend({ invoiceId: checkout.invoice_id, via: 'email', to: selectedCustomer.email, paymentUrl });
        }
        // Dismiss loading toast and show success toast - same as booking payment summary
        portalToast.dismiss(loadingToast);
        portalToast.success('Invoice sent successfully! ✅');
        console.log('✅ Product sale invoice email sent successfully');
        
        // Clear cart and close modals
        clearCart();
        setSelectedCustomer(null);
        setAppliedDiscount(null);
        setLoadingToastId(null);
        
        // Refresh stock items
        loadItems();
      } else {
        throw new Error(emailResult.error || 'Failed to send email');
      }

    } catch (error) {
      console.error('❌ Failed to send invoice email:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        selectedCustomer: selectedCustomer?.email,
        selectedBusiness: selectedBusiness?.id,
        cartLength: cart.length
      });
      
      // Dismiss loading toast and show error toast - same as booking payment summary
      portalToast.dismiss(loadingToast);
      portalToast.error('Failed to send invoice. Please try again.');
    } finally {
      setIsSendingInvoice(false);
      setLoadingToastId(null);
    }
  };

  const handleSendInvoiceSMS = async (
    customMessage: string,
    allowStripePay: boolean,
    depositAmount?: number,
    saveCardOnFile?: boolean,
  ) => {
    if (!selectedCustomer?.phone || !selectedBusiness) {
      portalToast.error('Missing customer phone or business info');
      return;
    }

    if (cart.length === 0) {
      portalToast.error('Cart is empty');
      return;
    }

    setIsInvoiceSMSModalOpen(false);

    try {
      setIsSendingInvoice(true);

      const loadingToast = portalToast.loading(
        <div>
          <div>Sending invoice via SMS...</div>
          <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>don't close store</div>
        </div>
      );

      const checkout = await runCheckout(null);
      const balanceDue = Math.max(checkout.total_cents - checkout.wallet_applied_cents, 0) / 100;
      const productSale = { id: checkout.sale_id, sale_number: checkout.sale_number, invoice_id: checkout.invoice_id };

      let paymentUrl: string | null = null;

      if (balanceDue > 0 && allowStripePay && (selectedBusiness as any).stripe_connect_account_id && (selectedBusiness as any).online_payments_enabled) {
        try {
          const { data: pendingPayment, error: paymentError } = await supabase
            .from('payments')
            .insert({
              business_id: selectedBusiness.id,
              booking_id: null,
              product_sale_id: productSale.id,
              invoice_id: productSale.invoice_id ?? null, // settle the counter-sale invoice on completion
              amount: balanceDue,
              method: 'credit_card',
              status: 'pending',
              reference: `Product Sale ${productSale.sale_number}`,
              notes: `Payment for product sale ${productSale.sale_number}`,
              payment_token: crypto.randomUUID(),
              save_card_on_file: !!(saveCardOnFile && selectedCustomer?.id && allowStripePay),
              save_card_customer_id:
                saveCardOnFile && selectedCustomer?.id && allowStripePay ? selectedCustomer.id : null,
            })
            .select()
            .single();

          if (!paymentError && pendingPayment) {
            const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
              body: { paymentId: pendingPayment.id }
            });
            if (!linkError && linkData?.paymentUrl) {
              paymentUrl = linkData.paymentUrl;
            }
          }
        } catch (paymentLinkError) {
          console.error('Error generating payment link for SMS:', paymentLinkError);
        }
      }

      let smsMessage = customMessage;
      if (paymentUrl) {
        smsMessage += ` Invoice #${checkout.invoice_number} — amount owing: $${balanceDue.toFixed(2)}. Pay here: ${paymentUrl}`;
      } else if (balanceDue <= 0) {
        smsMessage += ` Invoice #${checkout.invoice_number} is paid in full.`;
      } else {
        smsMessage += ` Invoice #${checkout.invoice_number} — amount owing: $${balanceDue.toFixed(2)}.`;
      }

      const result = await sendSMS({
        businessId: selectedBusiness.id,
        to: selectedCustomer.phone,
        message: smsMessage,
        customerId: selectedCustomer.id
      });

      if (result.success) {
        if (checkout.invoice_id) {
          await recordInvoiceSend({ invoiceId: checkout.invoice_id, via: 'sms', to: selectedCustomer.phone, paymentUrl });
        }
        portalToast.dismiss(loadingToast);
        portalToast.success('Invoice sent via SMS! ✅');
        clearCart();
        setSelectedCustomer(null);
        setAppliedDiscount(null);
        loadItems();
      } else {
        throw new Error(result.error || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('❌ Failed to send invoice SMS:', error);
      portalToast.error('Failed to send invoice via SMS. Please try again.');
    } finally {
      setIsSendingInvoice(false);
    }
  };

  const handleCustomerSelected = (customer: any) => {
    setSelectedCustomer(customer);
    setIsCustomerModalOpen(false);
    
    // If we were waiting to open send modal after customer selection, open the right one
    if (shouldOpenSendInvoiceAfterCustomer) {
      const method = shouldOpenSendInvoiceAfterCustomer;
      setShouldOpenSendInvoiceAfterCustomer(null);
      if (method === 'email') {
        setIsInvoiceEmailModalOpen(true);
      } else {
        setIsInvoiceSMSModalOpen(true);
      }
    }
    // Don't automatically open payment modal - let user choose when to take payment
  };

  return (
    <div className="h-full bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg sm:text-2xl font-semibold text-gray-900">Sell Products</h1>
              <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Add products to cart and complete sales</p>
            </div>
            
            {/* Cart Summary - Icon only on mobile, full text on desktop */}
            <button
              onClick={() => setIsCartOpen(!isCartOpen)}
              className="relative bg-blue-600 active:bg-blue-700 text-white p-2 sm:px-4 sm:py-2 rounded-xl flex items-center transition-colors touch-manipulation"
              style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
            >
              <ShoppingCart className="h-5 w-5" />
              <span className="hidden sm:inline-block sm:ml-2 font-semibold">Cart ({cart.length})</span>
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[11px] rounded-full h-5 w-5 flex items-center justify-center font-semibold">
                  {cart.reduce((total, item) => total + item.quantity, 0)}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Main Products Area */}
        <div className="flex-1 flex flex-col">
          {/* Search Bar */}
          <div className="bg-white border-b border-gray-200/50 px-4 sm:px-6 py-3 sm:py-4">
            <div className="relative">
              <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-2.5 bg-white border border-gray-300 rounded-xl text-sm sm:text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                style={{ minHeight: '44px' }}
              />
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 text-sm">Loading products...</p>
                </div>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <Package2 className="h-12 w-12 mb-4 text-gray-400" />
                <p className="text-lg font-semibold text-gray-900 mb-1">No products available</p>
                <p className="text-sm text-gray-600">No products found matching your search</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-6 gap-3 sm:gap-4">
                {filteredItems.map((item) => (
                  <div key={item.id} className="bg-white rounded-2xl border border-gray-200/50 shadow-sm active:shadow-md transition-shadow touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                    {/* Product Image */}
                    <div className="h-28 sm:h-36 bg-gray-100 rounded-t-2xl overflow-hidden">
                      {productPhotos[item.id] ? (
                        <img
                          src={productPhotos[item.id]}
                          alt={item.name}
                          className="w-full h-full object-contain hover:scale-105 transition-transform duration-200"
                          onError={(e) => {
                            // If image fails to load, hide it and show placeholder
                            e.currentTarget.style.display = 'none';
                            const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                            if (placeholder) placeholder.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ display: productPhotos[item.id] ? 'none' : 'flex' }}
                      >
                        <Package2 className="h-12 w-12 text-gray-400" />
                      </div>
                    </div>
                    
                    {/* Product Info */}
                    <div className="p-2.5 sm:p-3">
                      <h3 className="font-semibold text-gray-900 truncate text-xs sm:text-sm">{item.name}</h3>
                      {item.description && (
                        <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5 line-clamp-2">{item.description}</p>
                      )}
                      
                      <div className="mt-2 sm:mt-2.5">
                        <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                          <div className="flex-1 min-w-0">
                            {isRentalOnly(item) ? (
                              <p className="text-xs sm:text-base font-semibold text-indigo-600 truncate">{rentalRateLabel(item)}</p>
                            ) : (
                              <p className="text-xs sm:text-base font-semibold text-green-600 truncate">
                                ${item.price.toFixed(2)}
                                {isRentable(item) && (
                                  <span className="ml-1 text-[10px] sm:text-xs font-medium text-indigo-600">or {rentalRateLabel(item)}</span>
                                )}
                              </p>
                            )}
                            <p className="text-[10px] sm:text-xs text-gray-500">
                              {item.track_inventory === false ? 'Made to order' : `${item.quantity} in stock`}
                              {Number(item.deposit_cents) > 0 && ` · ${fmtCents(Number(item.deposit_cents))} deposit`}
                            </p>
                          </div>
                        </div>
                        
                        {(() => {
                          const cartItem = cart.find(cartItem => cartItem.product.id === item.id);
                          
                          if (cartItem) {
                            // Show quantity controls if item is in cart - full width on mobile
                            return (
                              <div className="flex items-center bg-blue-50 border border-blue-200 rounded-xl w-full">
                                <button
                                  onClick={() => updateCartQuantity(item.id, cartItem.quantity - 1)}
                                  className="flex-1 p-1.5 sm:p-2 text-blue-600 active:bg-blue-100 rounded-l-xl transition-colors flex items-center justify-center touch-manipulation"
                                  style={{ WebkitTapHighlightColor: 'transparent', minHeight: '36px' }}
                                >
                                  <Minus className="h-3 w-3 sm:h-4 sm:w-4" />
                                </button>
                                <span className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-blue-800 text-center min-w-[2rem] sm:min-w-[2.5rem]">
                                  {cartItem.quantity}
                                </span>
                                <button
                                  onClick={() => updateCartQuantity(item.id, cartItem.quantity + 1)}
                                  className="flex-1 p-1.5 sm:p-2 text-blue-600 active:bg-blue-100 rounded-r-xl transition-colors flex items-center justify-center touch-manipulation disabled:opacity-50"
                                  disabled={item.track_inventory !== false && item.allow_oversell !== true && cartItem.quantity >= item.quantity}
                                  style={{ WebkitTapHighlightColor: 'transparent', minHeight: '36px' }}
                                >
                                  <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                                </button>
                              </div>
                            );
                          } else {
                            // Show add to cart button if item is not in cart - full width on mobile
                            return (
                              <button
                                onClick={() => addToCart(item)}
                                className="bg-blue-600 active:bg-blue-700 text-white p-1.5 sm:p-2 rounded-xl transition-colors w-full flex items-center justify-center touch-manipulation disabled:opacity-50"
                                disabled={item.track_inventory !== false && item.allow_oversell !== true && item.quantity === 0}
                                style={{ WebkitTapHighlightColor: 'transparent', minHeight: '36px' }}
                              >
                                <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                              </button>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

         {/* Cart Modal */}
         {isCartOpen && (
           <div 
             className="fixed inset-0 transition-opacity duration-200 bg-black/50"
             style={{
               zIndex: 100010,
               backdropFilter: 'blur(8px)',
               WebkitBackdropFilter: 'blur(8px)'
             }}
             onClick={() => setIsCartOpen(false)}
           >
             <div 
               className="fixed inset-y-0 right-0 w-full md:w-[500px] bg-white shadow-2xl flex flex-col overflow-hidden transform transition-transform duration-200"
               style={{
                 fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                 zIndex: 100050
               }}
               onClick={(e) => e.stopPropagation()}
             >
               {/* Close Button */}
               <button
                 onClick={() => setIsCartOpen(false)}
                 className="absolute top-2.5 right-3 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
                 style={{ WebkitTapHighlightColor: 'transparent' }}
               >
                 <X className="h-5 w-5" />
               </button>

               {/* Cart Header */}
               <div className="bg-gray-50 border-b border-gray-200 px-6 py-3.5 text-center relative">
                 <div className="flex items-center justify-center gap-2">
                   {cart.length > 0 && (
                     <button
                       onClick={clearCart}
                       className="absolute left-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-red-600 rounded-xl hover:bg-red-50 active:bg-red-100 active:scale-95 transition-all duration-150 touch-manipulation"
                       title="Clear Cart"
                       style={{ WebkitTapHighlightColor: 'transparent' }}
                     >
                       <Trash2 className="h-5 w-5" />
                     </button>
                   )}
                   <h3 className="text-lg font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                     Cart
                     {cart.length > 0 && (
                       <span className="ml-1.5 text-sm font-semibold text-gray-400">
                         {cart.reduce((n, ci) => n + ci.quantity, 0)}
                       </span>
                     )}
                   </h3>
                 </div>
               </div>

               {/* Cart Items */}
               <div 
                 className="flex-1 overflow-y-auto bg-white"
                 style={{ WebkitOverflowScrolling: 'touch' }}
               >
                 <div className="p-3 sm:p-4">
                   {cart.length === 0 ? (
                     <div className="bg-white rounded-[12px] border border-gray-200/60 p-8 shadow-[0_1px_2px_rgba(0,0,0,0.05)] text-center">
                       <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                       <h3 className="text-sm font-semibold text-gray-900 mb-1">Your cart is empty</h3>
                       <p className="text-sm text-gray-500 mb-4">Add products to get started</p>
                     </div>
                   ) : (
                     <div className="space-y-2">
                       {cart.map((cartItem) => (
                       <div key={cartItem.product.id} className="flex items-center gap-3 p-2.5 bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                       <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                         {productPhotos[cartItem.product.id] ? (
                           <img
                             src={productPhotos[cartItem.product.id]}
                             alt={cartItem.product.name}
                             className="w-full h-full object-cover"
                           />
                         ) : (
                           <Package2 className="h-7 w-7 sm:h-8 sm:w-8 text-gray-400" />
                         )}
                       </div>
                       <div className="flex-1 min-w-0">
                         <h4 className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                           {cartItem.isRental && (
                             <span className="mr-1.5 inline-flex items-center rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 align-middle">
                               Rental
                             </span>
                           )}
                           {cartItem.product.name}
                         </h4>
                         <div className="flex items-center gap-2 mt-0.5">
                           <p className="text-xs sm:text-sm text-gray-600">
                             {cartItem.isRental
                               ? `$${rentalRate(cartItem.product).toFixed(2)} / ${rentalPeriod(cartItem.product)}`
                               : `$${cartItem.product.price?.toFixed(2) || '0.00'} each`}
                           </p>
                           {isRentable(cartItem.product) && Number(cartItem.product.price) > 0 && (
                             <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
                               <button
                                 type="button"
                                 onClick={() => setLineRental(cartItem.product.id, false)}
                                 className={`px-2 py-0.5 text-[11px] font-semibold ${!cartItem.isRental ? 'bg-gray-800 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                               >
                                 Buy
                               </button>
                               <button
                                 type="button"
                                 onClick={() => setLineRental(cartItem.product.id, true)}
                                 className={`px-2 py-0.5 text-[11px] font-semibold ${cartItem.isRental ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                               >
                                 Rent
                               </button>
                             </div>
                           )}
                         </div>
                       </div>
                       <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 shrink-0">
                         <button
                           onClick={() => updateCartQuantity(cartItem.product.id, cartItem.quantity - 1)}
                           aria-label="Decrease quantity"
                           className="w-9 h-9 text-gray-600 hover:text-red-600 active:bg-gray-100 rounded-l-lg flex items-center justify-center touch-manipulation"
                           style={{ WebkitTapHighlightColor: 'transparent' }}
                         >
                           <Minus className="h-4 w-4" />
                         </button>
                         <span className="font-semibold text-gray-900 w-7 text-center text-sm tabular-nums">{cartItem.quantity}</span>
                         <button
                           onClick={() => updateCartQuantity(cartItem.product.id, cartItem.quantity + 1)}
                           aria-label="Increase quantity"
                           className="w-9 h-9 text-gray-600 hover:text-green-600 active:bg-gray-100 rounded-r-lg flex items-center justify-center touch-manipulation"
                           style={{ WebkitTapHighlightColor: 'transparent' }}
                         >
                           <Plus className="h-4 w-4" />
                         </button>
                       </div>
                     </div>
                   ))}
                   </div>
                 )}

                 {/* Container deposits — same controls as the order Deliver step,
                     adjustable right here since the counter has no delivery step */}
                 {cart.length > 0 && cartQuote && cartQuote.deposits.length > 0 && (
                   <div className="mt-3 bg-white rounded-[12px] border border-gray-200/60 p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                     <div className="flex items-center gap-2 mb-1">
                       <RefreshCw className="h-4 w-4 text-blue-600" />
                       <h4 className="text-sm font-semibold text-gray-900">Container deposits</h4>
                     </div>
                     <p className="text-[11px] text-gray-500 mb-2">
                       Enter every empty the customer is handing back. Deposit is only charged on containers they keep; extra empties are refunded.
                       {!selectedCustomer && ' Add a customer to refund to their wallet or card.'}
                     </p>
                     <div className="space-y-2">
                       {cartQuote.deposits.map(d => {
                         const onHand = d.prior_balance ?? 0;
                         const cur = returns[d.stock_item_id] ?? { returned: String(d.returned), lost: String(d.lost) };
                         const setBoth = (returned: number, lost: number) =>
                           setReturns(prev => ({ ...prev, [d.stock_item_id]: { returned: String(returned), lost: String(lost), touched: true } }));
                         const r = Math.max(0, parseInt(cur.returned) || 0);
                         const l = Math.max(0, parseInt(cur.lost) || 0);
                         // Empties beyond what's on record still refund — at today's rate.
                         const unmatched = d.unmatched ?? 0;
                         return (
                           <div key={d.stock_item_id} className="rounded-xl border border-gray-200 p-3 space-y-2">
                             <div className="flex items-center justify-between gap-2">
                               <span className="text-[13px] font-medium text-gray-900 truncate">{d.product_name}</span>
                               <span className="text-[11px] text-gray-500 whitespace-nowrap">
                                 {d.delivered} out · {fmtCents(d.deposit_cents)}/deposit
                               </span>
                             </div>
                             <p className={`text-[12px] font-semibold ${onHand > 0 ? 'text-blue-700' : 'text-gray-500'}`}>
                               {selectedCustomer
                                 ? `Customer has ${onHand} on record${onHand > 0 ? ' — collect the empties' : ''}`
                                 : 'Walk-in — every empty handed back refunds at today\u2019s rate'}
                             </p>
                             <div className="flex flex-wrap gap-1.5">
                               {onHand > 0 && (
                                 <button
                                   type="button"
                                   onClick={() => setBoth(Math.min(d.delivered, onHand), 0)}
                                   className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                                 >
                                   Full swap ({Math.min(d.delivered, onHand)} back)
                                 </button>
                               )}
                               {onHand > 0 && onHand !== Math.min(d.delivered, onHand) && (
                                 <button
                                   type="button"
                                   onClick={() => setBoth(onHand, 0)}
                                   className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                                 >
                                   All {onHand} back
                                 </button>
                               )}
                               <button
                                 type="button"
                                 onClick={() => setBoth(d.delivered, 0)}
                                 className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                               >
                                 No deposit ({d.delivered} back)
                               </button>
                               <button
                                 type="button"
                                 onClick={() => setBoth(0, 0)}
                                 className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                               >
                                 None
                               </button>
                             </div>
                             <div className="flex items-center gap-3">
                               <label className="flex-1">
                                 <span className="block text-[11px] text-gray-500 mb-1">Empties returned</span>
                                 <input
                                   type="number"
                                   min="0"
                                   value={cur.returned}
                                   onChange={(e) => setReturns(prev => ({
                                     ...prev,
                                     [d.stock_item_id]: { returned: e.target.value, lost: prev[d.stock_item_id]?.lost ?? '0', touched: true },
                                   }))}
                                   className="w-full px-3 py-2 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                                   style={{ fontSize: '16px' }}
                                 />
                               </label>
                               <label className="flex-1">
                                 <span className="block text-[11px] text-gray-500 mb-1">Lost (deposit kept)</span>
                                 <input
                                   type="number"
                                   min="0"
                                   max={onHand}
                                   value={cur.lost}
                                   onChange={(e) => setReturns(prev => ({
                                     ...prev,
                                     [d.stock_item_id]: { returned: prev[d.stock_item_id]?.returned ?? '0', lost: e.target.value, touched: true },
                                   }))}
                                   className="w-full px-3 py-2 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                                   style={{ fontSize: '16px' }}
                                 />
                               </label>
                             </div>
                             <p className="text-[11px] text-gray-500">
                               {d.net_new > 0
                                 ? `${d.net_new} new deposit${d.net_new === 1 ? '' : 's'} charged (${fmtCents(d.net_new * d.deposit_cents)})`
                                 : d.excess_refund_cents > 0
                                 ? `${fmtCents(d.excess_refund_cents)} deposit refund due`
                                 : 'Even swap — no new deposit'}
                               {l > onHand && ` · lost capped at the ${onHand} on record`}
                             </p>
                             {unmatched > 0 && (
                               <p className="text-[11px] font-medium text-amber-700">
                                 {`${unmatched} of the ${r} picked up ${unmatched === 1 ? 'isn\u2019t' : 'aren\u2019t'} on ${selectedCustomer ? 'this customer\u2019s' : 'any'} record — refunded at today\u2019s ${fmtCents(d.unmatched_rate_cents ?? d.deposit_cents)} rate${selectedCustomer ? ' and the count is corrected.' : '.'}`}
                               </p>
                             )}
                           </div>
                         );
                       })}
                     </div>

                   </div>
                 )}

                 {hasRental && (
                   <div className="mt-3 rounded-[12px] border border-indigo-200 bg-indigo-50/50 p-3 text-[12px] text-indigo-900">
                     Rental gear goes out today on a recurring agreement: the first {cart.find(ci => ci.isRental) ? rentalPeriod(cart.find(ci => ci.isRental)!.product) : 'period'} bills on this sale
                     and billing continues automatically.
                     {!selectedCustomer && ' A customer is required for rentals.'}
                   </div>
                 )}
                 </div>
               </div>

               {/* Cart Footer — kept compact so the items stay on screen.
                   Customer + discount are one row of chips; the discount
                   editor only opens on demand; totals are a single breakdown
                   line above the total. */}
               {cart.length > 0 && (
               <div className="bg-white border-t border-gray-200 px-4 sm:px-5 pt-3 pb-4 space-y-3">
                 <div className="flex items-center gap-2">
                   {selectedCustomer ? (
                     <div className="flex-1 min-w-0 flex items-center gap-2 h-10 pl-1.5 pr-1 rounded-full bg-blue-50 border border-blue-200/60">
                       <div className="w-7 h-7 shrink-0 rounded-full bg-blue-600 text-white text-[11px] font-semibold flex items-center justify-center">
                         {selectedCustomer.first_name?.[0]}{selectedCustomer.last_name?.[0]}
                       </div>
                       <span className="flex-1 min-w-0 truncate text-[13px] font-semibold text-blue-900">
                         {`${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim() || selectedCustomer.email || 'Customer'}
                       </span>
                       <button
                         onClick={() => setSelectedCustomer(null)}
                         aria-label="Remove customer"
                         className="p-1.5 rounded-full text-blue-600 hover:bg-blue-100 active:bg-blue-200 touch-manipulation"
                         style={{ WebkitTapHighlightColor: 'transparent' }}
                       >
                         <X className="h-3.5 w-3.5" />
                       </button>
                     </div>
                   ) : (
                     <button
                       onClick={() => setIsCustomerModalOpen(true)}
                       className="flex-1 h-10 inline-flex items-center justify-center gap-1.5 rounded-full border border-dashed border-gray-300 text-[13px] font-semibold text-gray-600 hover:border-blue-400 hover:text-blue-600 active:bg-blue-50 transition-colors touch-manipulation"
                       style={{ WebkitTapHighlightColor: 'transparent' }}
                     >
                       <Plus className="h-4 w-4" />
                       Customer
                     </button>
                   )}

                   {appliedDiscount ? (
                     <div className="flex items-center gap-1 h-10 pl-3 pr-1 rounded-full bg-blue-50 border border-blue-200/60 max-w-[55%]">
                       <Tag className="h-3.5 w-3.5 shrink-0 text-blue-600" />
                       <span className="truncate text-[13px] font-semibold text-blue-900">
                         {appliedDiscount.source === 'code' ? appliedDiscount.code : appliedDiscount.description}
                       </span>
                       <button
                         onClick={removeDiscount}
                         aria-label="Remove discount"
                         className="p-1.5 rounded-full text-blue-600 hover:bg-blue-100 active:bg-blue-200 touch-manipulation"
                         style={{ WebkitTapHighlightColor: 'transparent' }}
                       >
                         <X className="h-3.5 w-3.5" />
                       </button>
                     </div>
                   ) : (
                     <button
                       onClick={() => { setShowDiscountEditor(v => !v); setDiscountError(null); }}
                       className={`h-10 inline-flex items-center gap-1.5 px-3.5 rounded-full border text-[13px] font-semibold transition-colors touch-manipulation ${
                         showDiscountEditor
                           ? 'border-blue-500 bg-blue-50 text-blue-700'
                           : 'border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600'
                       }`}
                       style={{ WebkitTapHighlightColor: 'transparent' }}
                     >
                       <Tag className="h-4 w-4" />
                       Discount
                     </button>
                   )}
                 </div>

                 {showDiscountEditor && !appliedDiscount && (
                   <div className="rounded-xl border border-gray-200 p-2 space-y-1.5">
                     <div className="flex gap-1.5">
                       <div className="flex shrink-0 bg-gray-100 rounded-lg p-0.5">
                         {(['code', 'custom'] as const).map(mode => (
                           <button
                             key={mode}
                             onClick={() => { setDiscountMode(mode); setDiscountError(null); }}
                             className={`px-2.5 h-9 text-[12px] font-semibold rounded-md transition-colors touch-manipulation ${
                               discountMode === mode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                             }`}
                             style={{ WebkitTapHighlightColor: 'transparent' }}
                           >
                             {mode === 'code' ? 'Code' : 'Custom'}
                           </button>
                         ))}
                       </div>
                       {discountMode === 'code' ? (
                         <input
                           type="text"
                           placeholder="Discount code"
                           value={discountCode}
                           onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                           onKeyPress={(e) => e.key === 'Enter' && !discountValidating && applyDiscountCode()}
                           disabled={discountValidating}
                           autoFocus
                           className="flex-1 min-w-0 h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 disabled:bg-gray-100 uppercase"
                           style={{ fontSize: '16px' }}
                         />
                       ) : (
                         <>
                           <input
                             type="number"
                             placeholder="Amount"
                             value={customDiscountAmount}
                             onChange={(e) => setCustomDiscountAmount(e.target.value)}
                             onKeyPress={(e) => e.key === 'Enter' && applyCustomDiscount()}
                             autoFocus
                             className="flex-1 min-w-0 h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                             style={{ fontSize: '16px' }}
                           />
                           <select
                             value={customDiscountType}
                             onChange={(e) => setCustomDiscountType(e.target.value as '%' | '$')}
                             className="h-10 px-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white"
                             style={{ fontSize: '16px' }}
                           >
                             <option value="%">%</option>
                             <option value="$">$</option>
                           </select>
                         </>
                       )}
                       <button
                         onClick={discountMode === 'code' ? applyDiscountCode : applyCustomDiscount}
                         disabled={discountValidating}
                         className="h-10 px-3.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-400 text-white text-[13px] font-semibold rounded-lg touch-manipulation"
                         style={{ WebkitTapHighlightColor: 'transparent' }}
                       >
                         {discountValidating ? '…' : 'Apply'}
                       </button>
                     </div>
                     {discountError && (
                       <p className="flex items-center gap-1 text-[12px] font-medium text-red-600">
                         <AlertCircle className="h-3.5 w-3.5" /> {discountError}
                       </p>
                     )}
                   </div>
                 )}

                 <div>
                   <p className="flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] text-gray-500">
                     <span>Subtotal ${cartSubtotal.toFixed(2)}</span>
                     {depositChargedCents > 0 && <span>Deposits {fmtCents(depositChargedCents)}</span>}
                     {invoiceCreditCents > 0 && <span className="text-emerald-600">Refund −{fmtCents(invoiceCreditCents)}</span>}
                     {appliedDiscount && <span className="text-blue-600">Discount −${discountAmount.toFixed(2)}</span>}
                     <span>{taxLabel} ${cartTaxAmount.toFixed(2)}</span>
                   </p>
                   <div className="flex items-baseline justify-between mt-0.5">
                     <span className="text-[15px] font-bold text-gray-900">Total</span>
                     <span className="text-xl font-bold text-blue-600" style={{ letterSpacing: '-0.01em' }}>${cartTotal.toFixed(2)}</span>
                   </div>
                   {/* Deposit refund — pre-chosen (wallet for customers, cash for
                       walk-ins); the pill drops down to change it. */}
                   {excessRefundCents > 0 && selectedBusiness && (
                     <div className="mt-1.5 rounded-lg border border-emerald-200/70 bg-emerald-50/40 px-2.5 py-1.5">
                       <RefundDestinationSelect
                         businessId={selectedBusiness.id}
                         customerId={selectedCustomer?.id ?? null}
                         amountCents={excessRefundCents}
                         value={refundMode}
                         onChange={next => setRefundMode(next as CounterRefundMode)}
                         destinations={selectedCustomer
                           ? ['wallet', 'card', 'cash', 'e_transfer', 'invoice']
                           : ['cash', 'e_transfer', 'invoice', 'card', 'wallet']}
                         reference={refundReference}
                         onReferenceChange={setRefundReference}
                         invoiceLabel="Credit on this sale"
                         direction="up"
                       />
                       {refundMode === 'invoice' && excessRefundCents > invoiceCreditCents && (
                         <p className="mt-0.5 text-[11px] text-gray-500">
                           {fmtCents(invoiceCreditCents)} fits on this sale; the remaining {fmtCents(excessRefundCents - invoiceCreditCents)} becomes wallet credit.
                         </p>
                       )}
                     </div>
                   )}
                   {walletWillApplyCents > 0 && (
                     <div className="flex items-baseline justify-between mt-1 text-[13px]">
                       <span className="font-semibold text-emerald-600">Wallet credit −{fmtCents(walletWillApplyCents)}</span>
                       <span className="font-bold text-gray-900">To collect ${amountToCollect.toFixed(2)}</span>
                     </div>
                   )}
                 </div>

                 <div className="flex gap-3">
                   <div className="relative flex-1">
                     <button
                       onClick={(e) => {
                         e.stopPropagation();
                         if (cart.length === 0) return;
                         setShowSendDropdown(!showSendDropdown);
                       }}
                       disabled={cart.length === 0 || isSendingInvoice}
                       className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-3 px-4 rounded-xl font-semibold transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2 touch-manipulation"
                       style={{ WebkitTapHighlightColor: 'transparent', minHeight: '44px' }}
                     >
                       {isSendingInvoice ? (
                         <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                       ) : (
                         <Send className="h-4 w-4" />
                       )}
                       <span className="text-sm sm:text-base">{isSendingInvoice ? 'Sending...' : 'Send'}</span>
                     </button>

                     {showSendDropdown && (
                       <div
                         className="absolute left-0 bottom-full mb-2 w-48 rounded-xl shadow-lg bg-white border border-gray-200 z-20 overflow-hidden"
                         onMouseDown={(e) => e.stopPropagation()}
                         onClick={(e) => e.stopPropagation()}
                       >
                         <button
                           onClick={() => {
                             setShowSendDropdown(false);
                             handleSendInvoiceVia('email');
                           }}
                           className="w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center transition-all duration-150 touch-manipulation"
                           style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                         >
                           <Mail className="h-4 w-4 mr-3 text-blue-500" />
                           Email
                         </button>
                         <button
                           onClick={() => {
                             setShowSendDropdown(false);
                             handleSendInvoiceVia('sms');
                           }}
                           className="w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center transition-all duration-150 touch-manipulation border-t border-gray-100"
                           style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                         >
                           <MessageSquare className="h-4 w-4 mr-3 text-green-500" />
                           SMS
                         </button>
                       </div>
                     )}
                   </div>
                   {cartQuote && amountToCollect <= 0 ? (
                     <button
                       onClick={completeWithWallet}
                       disabled={isCompletingWithWallet || (hasRental && !selectedCustomer)}
                       className="flex-1 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:bg-gray-400 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2 touch-manipulation"
                       style={{ WebkitTapHighlightColor: 'transparent', minHeight: '44px' }}
                     >
                       {isCompletingWithWallet ? (
                         <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                       ) : (
                         <Check className="h-4 w-4" />
                       )}
                       <span className="text-sm sm:text-base">{walletWillApplyCents > 0 ? 'Complete with wallet' : 'Complete sale'}</span>
                     </button>
                   ) : (
                     <button
                       onClick={() => {
                         // Keep cart open when opening payment modal
                         setIsPaymentModalOpen(true);
                       }}
                       disabled={!cartQuote || (hasRental && !selectedCustomer)}
                       className="flex-1 bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-3 px-4 rounded-xl font-semibold transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2 touch-manipulation"
                       style={{ WebkitTapHighlightColor: 'transparent', minHeight: '44px' }}
                     >
                       <CreditCard className="h-4 w-4" />
                       <span className="text-sm sm:text-base">Take Payment</span>
                     </button>
                   )}
                 </div>
               </div>
               )}
             </div>
           </div>
         )}
      </div>

      {/* Modals */}
      {isCustomerModalOpen && (
        <SelectCustomerModal
          isOpen={isCustomerModalOpen}
          onClose={() => setIsCustomerModalOpen(false)}
          onSelect={handleCustomerSelected}
        />
      )}

       {isPaymentModalOpen && (
         <RecordProductSalePaymentModal
           isOpen={isPaymentModalOpen}
           onClose={() => setIsPaymentModalOpen(false)}
           onSuccess={() => {
             setIsPaymentModalOpen(false);
             clearCart();
             setSelectedCustomer(null);
             setAppliedDiscount(null);
             // Refresh stock items to update quantities
             loadItems();
           }}
           businessId={selectedBusiness?.id || ''}
           cart={cart}
           selectedCustomer={selectedCustomer}
           appliedDiscount={appliedDiscount}
           subtotal={cartSubtotal}
           discountAmount={discountAmount}
           taxAmount={cartTaxAmount}
           taxRatePercent={cartQuote?.combinedRatePercent}
           total={amountToCollect}
           suggestSaveCard={hasRental}
           createSale={async (payment) => {
             const res = await runCheckout(payment);
             return { id: res.sale_id, sale_number: res.sale_number, invoice_id: res.invoice_id };
           }}
         />
       )}

      <InvoiceEmailModal
        isOpen={isInvoiceEmailModalOpen}
        onClose={() => setIsInvoiceEmailModalOpen(false)}
        onSend={handleSendInvoiceEmail}
        customerEmail={selectedCustomer?.email || ''}
        customerName={selectedCustomer ? `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim() || 'Customer' : 'Customer'}
        businessName={selectedBusiness?.name || 'Business'}
        bookingNumber={0}
        hasStripeConnected={!!(selectedBusiness as any)?.stripe_connect_account_id && (selectedBusiness as any)?.online_payments_enabled !== false}
        amountOwing={amountToCollect}
        canOfferSaveCardOnFile={!!selectedCustomer?.id}
        isSending={isSendingInvoice}
        initialSubject={`Product Invoice from ${selectedBusiness?.name || 'Business'}`}
        initialMessage={`Hi ${selectedCustomer ? `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim() || 'Customer' : 'Customer'},\n\nPlease find attached your product invoice.\n\nIf you have any questions regarding this invoice, please don't hesitate to get in touch with us.\n\nThank you,\n${selectedBusiness?.name || 'Business'}`}
      />
      <InvoiceSMSModal
        isOpen={isInvoiceSMSModalOpen}
        onClose={() => setIsInvoiceSMSModalOpen(false)}
        onSend={handleSendInvoiceSMS}
        customerPhone={selectedCustomer?.phone || ''}
        customerName={selectedCustomer?.first_name || 'there'}
        businessName={selectedBusiness?.name || 'Business'}
        bookingNumber={0}
        hasStripeConnected={!!(selectedBusiness as any)?.stripe_connect_account_id && (selectedBusiness as any)?.online_payments_enabled !== false}
        amountOwing={amountToCollect}
        canOfferSaveCardOnFile={!!selectedCustomer?.id}
        isSending={isSendingInvoice}
        initialMessage={`Hi ${selectedCustomer?.first_name || 'there'}, here is your product invoice from ${selectedBusiness?.name || 'Business'}.`}
      />

      {/* Portal Toast for Modal Context */}
      {showPortalToast && (
        <PortalToast
          type={portalToastType}
          onClose={() => setShowPortalToast(false)}
        >
          {portalToastMessage}
        </PortalToast>
      )}
    </div>
  );
}

export default SellProducts;
